/**
 * App-wide zoom lock, so the console behaves like an installed app on a phone
 * rather than a zoomable web page.
 *
 * The viewport meta tag alone does not do this. `user-scalable=no` and
 * `maximum-scale` in the viewport tag have been **ignored by iOS Safari since
 * iOS 10** — Apple removed author control of scaling for accessibility — so on
 * an iPhone the tag suppresses nothing at all. It is still worth declaring
 * because Android Chrome and Firefox do honour it; iOS needs the JS guard
 * below. That split is why this cannot be a metadata-only change.
 *
 * Three gestures can scale the page, each needing its own suppression:
 *  - Pinch, reported as multi-finger `touchmove` (Chromium, Firefox).
 *  - Pinch, reported as WebKit's proprietary `gesture*` events (Safari). These
 *    fire in addition to the touch events and at scale 1, so both are needed.
 *  - Double-tap, suppressed in CSS via `touch-action: manipulation` on the root
 *    (see App.css) — it is not an event we can cancel here.
 *
 * Desktop is deliberately untouched: browser zoom (Cmd/Ctrl +/-) is not
 * cancellable from script and must keep working, so the guard installs only on
 * a coarse-pointer device. A phone user's escape hatch is the browser's own
 * request-desktop-site / reader affordances, the same as any native app's
 * system text-size settings.
 *
 * The guard is reference counted because `useBodyScrollLock` also holds one for
 * an open modal's lifetime. Once installed globally the count never drops to
 * zero, so a closing modal cannot unpin the page-wide lock.
 */
let guardCount = 0;

/** Cancel pinch: any touchmove with more than one finger zooms/pans the page. */
function blockMultiTouchMove(event: TouchEvent): void {
  if (event.touches.length > 1) {
    event.preventDefault();
  }
}

/** Cancel Safari's proprietary pinch gesture events (fired even at scale 1). */
function blockGesture(event: Event): void {
  event.preventDefault();
}

/**
 * Start cancelling pinch gestures, and return a release for the caller's hold.
 * Non-passive listeners: `preventDefault` is what suppresses the browser's
 * scaling default action, and a passive listener cannot call it.
 */
export function acquirePinchGuard(): void {
  guardCount += 1;
  if (guardCount > 1) return;
  document.addEventListener('touchmove', blockMultiTouchMove, {
    passive: false,
  });
  document.addEventListener('gesturestart', blockGesture, { passive: false });
  document.addEventListener('gesturechange', blockGesture, { passive: false });
  document.addEventListener('gestureend', blockGesture, { passive: false });
}

export function releasePinchGuard(): void {
  guardCount = Math.max(0, guardCount - 1);
  if (guardCount > 0) return;
  document.removeEventListener('touchmove', blockMultiTouchMove);
  document.removeEventListener('gesturestart', blockGesture);
  document.removeEventListener('gesturechange', blockGesture);
  document.removeEventListener('gestureend', blockGesture);
}

/** True for phones and tablets; false for a mouse-driven desktop browser. */
function isTouchFirstDevice(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * Hold the pinch guard for the life of the document on touch-first devices.
 * Called once at startup; never released.
 */
export function installAppZoomLock(): void {
  if (!isTouchFirstDevice()) return;
  acquirePinchGuard();
}
